import { classifyTrpcError } from "./classifyTrpcError";
import { type CoalesceFn, SyncQueue } from "./SyncQueue";
import { SendOutcome, type SendResult, SyncWorker } from "./SyncWorker";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SyncFeatureOptions<TPayload, TResult> {
  // localStorage key used to persist the queue between sessions.
  storageKey: string;
  // Coalescing rule used when enqueueing a new payload — see
  // SyncQueueOptions.coalesce for semantics.
  coalesce: CoalesceFn<TPayload>;
  // Network call to send a single queued payload. Throw to signal failure;
  // the factory classifies the error into a SendOutcome.
  send: (payload: TPayload) => Promise<TResult>;
  // Optional callback invoked when a send succeeds and the in-flight head
  // was not coalesced during flight. Use this to reconcile UI with server
  // state.
  onSuccess?: (result: TResult, sentPayload: TPayload) => void;
  // Optional override for the storage backend; defaults to localStorage.
  storage?: StorageLike;
  // Optional override for online detection; defaults to navigator.onLine.
  isOnline?: () => boolean;
  // Extra commit gate checked after a send resolves (see FlushOnceArgs):
  // when it returns false the flush result is not committed and the entry
  // stays queued for a later worker. Used to keep an in-flight send that
  // raced an identity flip (impersonation) from being treated as delivered.
  shouldCommit?: () => boolean;
  // Background tick cadence when the queue is empty. Default 30s.
  tickIntervalMs?: number;
  // Cap on exponential backoff. Default 60s.
  maxBackoffMs?: number;
}

// A feature only owns its outbound write path (queue + worker). Reads are
// done in bulk elsewhere (a single batched query), so there's no per-feature
// hydrate here.
export interface SyncFeature<TPayload> {
  enqueue(payload: TPayload): void;
  start(): void;
  stop(): void;
}

function defaultIsOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

export function createSyncFeature<TPayload, TResult>(
  opts: SyncFeatureOptions<TPayload, TResult>,
): SyncFeature<TPayload> {
  const storage = opts.storage ?? localStorage;

  const queue = new SyncQueue<TPayload>({
    storage,
    storageKey: opts.storageKey,
    coalesce: opts.coalesce,
  });

  const send = async (payload: TPayload): Promise<SendResult<TResult>> => {
    try {
      const result = await opts.send(payload);

      return { outcome: SendOutcome.Ok, result };
    } catch (err) {
      const outcome = classifyTrpcError(err);

      if (outcome === SendOutcome.ClientError) {
        return { outcome: SendOutcome.ClientError };
      }

      return { outcome: SendOutcome.Transient };
    }
  };

  const worker = new SyncWorker<TPayload, TResult>({
    queue,
    send,
    onSuccess: opts.onSuccess,
    isOnline: opts.isOnline ?? defaultIsOnline,
    shouldCommit: opts.shouldCommit,
    intervalMs: opts.tickIntervalMs,
    maxBackoffMs: opts.maxBackoffMs,
  });

  return {
    enqueue(payload) {
      queue.enqueue(payload);
      worker.kick();
    },
    start() {
      worker.start();
    },
    stop() {
      worker.stop();
    },
  };
}
